import { type SVGProps } from 'react'
import { twMerge as twm } from 'tailwind-merge'

import { icons } from '../icons/desktop-v2'

// When adding new icons, run `node replace-hardcoded-icon-colors` script to swap out any hardcoded
// stroke colors with `currentColor` so that we can programmatically change the color.
//
// You can regenerate the icon barrel file by running `node generate-icon-barrel-file.js`.

type IconNameAndCategory =
    | [keyof typeof icons.large, 'large']
    | [keyof typeof icons.medium, 'medium']
    | [keyof typeof icons.small, 'small']
    | [keyof typeof icons.payment, 'payment']

export type IconName = IconNameAndCategory[0]
export type IconNameAndCategoryString = `${IconNameAndCategory[0]}.${IconNameAndCategory[1]}`

type Props = {
    icon: IconNameAndCategoryString
    background?: 'round' | 'square'
    backgroundColorClassName?: string
    svgClassName?: string
    className?: string
    style?: React.CSSProperties
    onClick?: () => void
    shouldInheritColor?: boolean
    hasHoverEffect?: boolean
}

const categorySizeClasses: {
    [K in keyof typeof icons]: [string, string]
} = {
    small: ['size-4', 'size-6'],
    medium: ['size-5', 'size-9'],
    large: ['size-6', 'size-12'],
    payment: ['w-[34px] h-[24px]', 'size-[68px]'],
}

export const Icon = ({
    background,
    backgroundColorClassName,
    className,
    svgClassName,
    onClick,
    icon,
    shouldInheritColor = false,
    style,
    hasHoverEffect = false,
}: Props) => {
    const [iconName, iconCategory] = icon.split('.') as IconNameAndCategory

    const IconComponent = (icons[iconCategory] as any)[iconName] as React.FC<
        SVGProps<SVGSVGElement>
    >

    const [sizeClassName, bgSizeClassName] = categorySizeClasses[iconCategory]

    if (background !== undefined) {
        return (
            <div
                className={twm(
                    'bg-primary-100 group relative flex items-center justify-center',
                    backgroundColorClassName,
                    background === 'round' ? 'rounded-full' : 'rounded-sm',
                    bgSizeClassName,
                    className
                )}
                onClick={onClick}
                style={style}
            >
                <IconComponent
                    className={twm(
                        iconCategory !== 'payment' && 'stroke-current',
                        !shouldInheritColor && 'text-primary-700',
                        sizeClassName,
                        svgClassName
                    )}
                />

                {hasHoverEffect && (
                    <div className="absolute inset-0 rounded-full group-hover:bg-black/20" />
                )}
            </div>
        )
    }
    else {
        return (
            <IconComponent
                className={twm(
                    iconCategory !== 'payment' && 'stroke-current',
                    !shouldInheritColor && 'text-primary-700',
                    sizeClassName,
                    svgClassName,
                    className
                )}
                onClick={onClick}
            />
        )
    }
}
